import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  MapPin, Star, BookOpen, Languages, Clock, GraduationCap,
  ChevronLeft, ShieldCheck, MessageSquare, Sparkles, Loader2, Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import BookingDialog from "../components/BookingDialog";
import { API, useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

const Stars = ({ value = 0, size = 16, onPick }) => {
  const v = Math.round(value);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= v;
        return (
          <Star
            key={i}
            className={`${filled ? "text-yellow-500 fill-yellow-500" : "text-gray-300"} ${onPick ? "cursor-pointer hover:scale-110 transition-transform" : ""}`}
            style={{ width: size, height: size }}
            onClick={onPick ? () => onPick(i) : undefined}
            data-testid={onPick ? `star-${i}` : undefined}
          />
        );
      })}
    </div>
  );
};

export default function EducatorProfile() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en") ? "en-US" : "fr-FR";
  const fmtXof = useFmtXof();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [educator, setEducator] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);

  // Review form
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isParent = user && typeof user === "object" && user.role === "parent";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eduRes, rvRes] = await Promise.all([
        axios.get(`${API}/educators/${id}`),
        axios.get(`${API}/educators/${id}/reviews`),
      ]);
      setEducator(eduRes.data);
      setReviews(rvRes.data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      navigate("/educators-list");
      return;
    } finally {
      setLoading(false);
    }
    if (isParent) {
      try {
        const { data } = await axios.get(`${API}/educators/${id}/eligibility`);
        setEligibility(data);
      } catch {
        setEligibility({ eligible: false });
      }
    }
  }, [id, navigate, isParent]);

  useEffect(() => { load(); }, [load]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!eligibility?.eligible || !comment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/educators/${id}/reviews`, {
        rating,
        comment: comment.trim(),
        booking_id: eligibility.booking_id,
      });
      toast.success(t("educatorProfile.thanksReview"));
      setComment("");
      setRating(5);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !educator) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#16A34A]" />
      </div>
    );
  }

  const p = educator.profile || {};
  const avg = educator.review_average ?? p.rating ?? 5;
  const count = educator.review_count ?? 0;

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="educator-profile-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Button asChild variant="ghost" size="sm" className="mb-4 text-[#0A2518]" data-testid="profile-back-btn">
          <Link to="/educators-list"><ChevronLeft className="h-4 w-4 mr-1" /> {t("educatorProfile.back")}</Link>
        </Button>

        {/* Header */}
        <Card className="p-8 border-[#E5EBE7] rounded-3xl relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/12 h-72 w-72 -top-20 -right-20" aria-hidden />
          <div className="relative grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <span className="pill mb-3"><GraduationCap className="h-3.5 w-3.5" /> {t("educatorProfile.verified")}</span>
                  <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0A2518]" data-testid="profile-name">{educator.name}</h1>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#4B5563]">
                    {p.city && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#16A34A]" />{p.city}</span>}
                    <span className="flex items-center gap-1.5">
                      <Stars value={avg} />
                      <span className="font-medium text-[#0A2518]">{Number(avg).toFixed(1)}</span>
                      <span>· {t("educatorProfile.reviews", { count })}</span>
                    </span>
                    {p.verified && (
                      <Badge className="bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/30">
                        <ShieldCheck className="h-3 w-3 mr-1" />{t("educatorProfile.verifiedBadge")}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-bold text-[#15803D]" data-testid="profile-rate">{fmtXof(p.hourly_rate)}</div>
                  <div className="text-xs text-[#4B5563]">{t("educatorProfile.perHour")}</div>
                </div>
              </div>

              <p className="mt-6 text-[#0A2518] leading-relaxed" data-testid="profile-bio">{p.bio}</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <Info icon={BookOpen} label={t("educatorProfile.subjects")} value={(p.subjects || []).join(", ") || "—"} />
                <Info icon={GraduationCap} label={t("educatorProfile.levels")} value={(p.levels || []).join(", ") || "—"} />
                <Info icon={Clock} label={t("educatorProfile.experience")} value={t("educatorProfile.experienceYears", { years: p.experience_years || 0 })} />
                <Info icon={Languages} label={t("educatorProfile.languages")} value={(p.languages || []).join(", ") || "—"} />
                <Info icon={Sparkles} label={t("educatorProfile.education")} value={p.education || "—"} />
                <Info icon={Clock} label={t("educatorProfile.availability")} value={p.availability || "—"} />
              </div>
            </div>

            {/* Side actions */}
            <div className="space-y-4">
              {isParent ? (
                <>
                  <Button onClick={() => setBookingOpen(true)} className="w-full rounded-full h-12 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="profile-book-btn">
                    {t("educatorProfile.book")}
                  </Button>
                  <Button asChild variant="outline" className="w-full rounded-full h-12 border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]" data-testid="profile-message-btn">
                    <Link to={`/messages?to=${educator.id}`}>{t("educatorProfile.message")}</Link>
                  </Button>
                </>
              ) : (
                <Button asChild className="w-full rounded-full h-12 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="profile-login-to-book">
                  <Link to="/login">{t("educatorProfile.loginToBook")}</Link>
                </Button>
              )}
              <Button asChild variant="outline" className="w-full rounded-full h-12 border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]">
                <Link to="/match"><Sparkles className="h-4 w-4 mr-2" /> {t("educatorProfile.aimatchCta")}</Link>
              </Button>
              <div className="text-xs text-[#4B5563] bg-[#F0FDF4] border border-[#16A34A]/15 rounded-xl p-3">
                <strong className="text-[#15803D]">{t("educatorProfile.securePay")}</strong>{t("educatorProfile.securePayText")}
              </div>
            </div>
          </div>
        </Card>

        {/* Reviews */}
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4" data-testid="profile-reviews">
            <h2 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#16A34A]" /> {t("educatorProfile.reviewsTitle")}
              {count > 0 && <span className="text-sm font-normal text-[#4B5563]">({count})</span>}
            </h2>

            {reviews.length === 0 ? (
              <Card className="p-6 border-[#E5EBE7] text-center text-[#4B5563]">
                {t("educatorProfile.noReviews")}
              </Card>
            ) : (
              reviews.map((r) => (
                <Card key={r.id} className="p-6 border-[#E5EBE7] rounded-2xl" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F0FDF4] text-[#15803D] font-display font-semibold">
                        {(r.parent_name || "P")[0]}
                      </div>
                      <div>
                        <div className="font-medium text-[#0A2518]">{r.parent_name}</div>
                        <div className="text-xs text-[#4B5563]">{r.subject ? t("educatorProfile.subjectPrefix", { subject: r.subject }) : ""}{new Date(r.created_at).toLocaleDateString(locale)}</div>
                      </div>
                    </div>
                    <Stars value={r.rating} />
                  </div>
                  <p className="mt-3 text-[#0A2518] leading-relaxed">{r.comment}</p>
                </Card>
              ))
            )}
          </div>

          {/* Review form / eligibility CTA */}
          <Card className="p-6 border-[#E5EBE7] rounded-2xl h-fit sticky top-24" data-testid="profile-review-form">
            <h3 className="font-display text-xl font-semibold text-[#0A2518] mb-2">{t("educatorProfile.leaveReview")}</h3>

            {!isParent ? (
              <p className="text-sm text-[#4B5563]">{t("educatorProfile.loginAsParent")}</p>
            ) : eligibility?.eligible ? (
              <form onSubmit={submitReview} className="space-y-4 mt-2" data-testid="review-form">
                <div>
                  <Label className="text-[#0A2518]">{t("educatorProfile.yourRating")}</Label>
                  <div className="mt-1.5"><Stars value={rating} size={28} onPick={setRating} /></div>
                </div>
                <div>
                  <Label className="text-[#0A2518]">{t("educatorProfile.yourTestimonial")}</Label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t("educatorProfile.testimonialPh")}
                    minLength={10}
                    maxLength={1500}
                    className="mt-1.5 min-h-[120px]"
                    data-testid="review-comment-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting || comment.trim().length < 10}
                  className="w-full rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white"
                  data-testid="review-submit-btn"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("educatorProfile.publishing")}</>) : (<><Send className="h-4 w-4 mr-2" /> {t("educatorProfile.publish")}</>)}
                </Button>
              </form>
            ) : eligibility?.reason === "no_paid_booking" ? (
              <p className="text-sm text-[#4B5563]">
                {t("educatorProfile.afterFirstPaidStart")}<strong className="text-[#0A2518]">{t("educatorProfile.afterFirstPaidEm")}</strong>{t("educatorProfile.afterFirstPaidEnd")}
              </p>
            ) : eligibility?.reason === "already_reviewed" ? (
              <p className="text-sm text-[#4B5563]">{t("educatorProfile.alreadyReviewed")}</p>
            ) : (
              <p className="text-sm text-[#4B5563]">{t("educatorProfile.checkingEligibility")}</p>
            )}
          </Card>
        </div>
      </div>

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} educator={educator} />
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#F0FDF4] ring-1 ring-[#16A34A]/15 shrink-0">
      <Icon className="h-4 w-4 text-[#16A34A]" />
    </span>
    <div>
      <div className="text-xs uppercase tracking-wide text-[#4B5563]">{label}</div>
      <div className="text-[#0A2518]">{value}</div>
    </div>
  </div>
);
